import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppIndex, AppRow } from "./AppIndex";
import { AppSearch } from "./AppSearch";
import { AppAssistApi } from "./AppAssistApi";
import { AppTaskApi, AppTaskRow } from "./AppTaskApi";
import { AppTaskTips } from "./AppTaskTips";
import { AppTask } from "./AppTask";
import { AppTaskList } from "./AppTaskList";

// AppTaskRules - what a task IS on the page: which bucket it sits in, in what order, what its due
// chip says, who has to act, and whether the new task UI may be shown at all. Pure: no DOM, no
// `window`, no transport - the Bridge suite may import it, and every rule is tested without a browser.
//
// ⭐ OWNER, 2026-09-26: tasks must be far more visible and interactive; the human state (done,
// snoozed, dismissed) lives in `tasks.json` in each person folder, written by the service
// (`POST /tasks`); replies owed count as tasks; no digest.
//
// ⛔ THE SWITCH (`Enabled`). The service writes the new fields only with `WAC_TASKS=1`, and nothing
// may change for the live user before our own deploy. So the new Tasks tab, the Today strip, the
// contact-page task actions and the task presets appear ONLY when the loaded index carries at least
// one row with a string `id` AND `state`. Any other index gets exactly today's Tasks view.
//
// ⛔ "TODAY" IS THE BROWSER'S DAY, NEVER UTC. The owner is UTC+7: at 20:00Z it is already tomorrow
// for them, and a task due "today" read in UTC would sit in Overdue for seven hours every morning.
// `TodayLocal` asks the browser's own offset; everything else takes the day as a "YYYY-MM-DD" string.

/** One section of the Tasks tab. */
export class AppTaskGroup {
    public Key: string = "";
    public Title: string = "";
    public Rows: AppRow[] = [];
}

/** The due chip: its words and its class (`overdue`, `today`, `soon`, `later`). */
export class AppTaskChip {
    public Text: string = "";
    public Cls: string = "";
}

/** The Today strip's content: at most `StripMax` rows, and how many there are in all. */
export class AppTaskStrip {
    public Rows: AppRow[] = [];
    public Count: number = 0;
}

export class AppTaskRules {
    /** The sections, in the order they are shown. */
    /** ⭐ "reconnect" (2026-09-27): the `q-` suggestions, below the work, never in the Today strip. */
    public static readonly Order: string[] = ["overdue", "today", "replies", "week", "later", "nodate", "waiting",
        "reconnect", "older", "snoozed", "done"];
    public static readonly Titles: Record<string, string> = {
        overdue: "Overdue", today: "Today", replies: "Replies owed", week: "This week", later: "Later",
        nodate: "No date", waiting: "Waiting on them", reconnect: "Reconnect", older: "Older", snoozed: "Snoozed",
        done: "Done",
    };
    /** The sections drawn folded (a `<details>`) until the owner opens them. */
    public static readonly Folded: string[] = ["older", "done"];
    /**
     * ⭐ OWNER, 2026-09-26: "Reply to Tim, 2 mo ago" under Today is noise. A reply owed is TODAY's only
     * while their last message is at most this many days old - in the BROWSER's days; older ones are
     * listed only under "Replies owed".
     */
    public static readonly ReplyTodayDays: number = 2;
    /** ⭐ Same day: an open task with NO due date whose evidence is older than this folds into "Older". */
    public static readonly OlderDays: number = 90;
    public static readonly Filters: string[] = ["mine", "theirs", "all"];
    public static readonly StripMax: number = 5;
    private static readonly Days: string[] = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    private static readonly Months: string[] = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug",
        "Sep", "Oct", "Nov", "Dec"];

    // ── The switch ───────────────────────────────────────────────────────────────────────────

    /**
     * ⛔ THE NEW TASK UI IS SHOWN ONLY FOR AN INDEX THAT CARRIES THE NEW FIELDS - at least one task
     * row with a non-empty `id` and `state`. An older index (or a service without `WAC_TASKS=1`)
     * keeps today's view byte for byte.
     */
    public static Enabled = (Index: AppIndex): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Index.Loaded === false) { return false; }
            for (const One of Index.Tasks) {
                if (One.Id.length > 0 && One.State.length > 0) { return true; }
            }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.Enabled");
            return false;
        }
    };

    /** ⚠ A ROW WITHOUT A SERVER ID CANNOT BE CHANGED - its buttons are not drawn. */
    public static CanAct = (Row: AppRow): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return Row.Id.length > 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.CanAct");
            return false;
        }
    };

    /** A key for the row in markup: its id, or one derived from its position - FOR DISPLAY ONLY. */
    public static KeyOf = (Row: AppRow, At: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Row.Id.length > 0) { return Row.Id; }
            return "k-" + String(At);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.KeyOf");
            return "";
        }
    };

    // ── Days, in the browser's time zone ─────────────────────────────────────────────────────

    /** "YYYY-MM-DD" of the instant `Ms` at a UTC offset of `OffsetMin` minutes (+420 for UTC+7). */
    public static LocalDay = (Ms: number, OffsetMin: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return new Date(Ms + OffsetMin * 60000).toISOString().substring(0, 10);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.LocalDay");
            return "";
        }
    };

    /** Today in THIS browser's time zone. ⛔ Never `toISOString()` of now - that is UTC's day. */
    public static TodayLocal = (Now: Date): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppTaskRules.LocalDay(Now.getTime(), -Now.getTimezoneOffset());
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.TodayLocal");
            return "";
        }
    };

    /** A real calendar day written "YYYY-MM-DD" - "2026-02-30" is not one. */
    public static IsDay = (Text: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (/^\d{4}-\d{2}-\d{2}$/.test(nz(Text)) === false) { return false; }
            const Ms: number = Date.UTC(Number(Text.substring(0, 4)), Number(Text.substring(5, 7)) - 1,
                Number(Text.substring(8, 10)));
            return new Date(Ms).toISOString().substring(0, 10) === Text;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.IsDay");
            return false;
        }
    };

    /** `Day` plus `N` calendar days (N may be negative). */
    public static AddDays = (Day: string, N: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppTaskRules.IsDay(Day) === false) { return ""; }
            return new Date(AppTaskRules.MsOf(Day) + N * 86400000).toISOString().substring(0, 10);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.AddDays");
            return "";
        }
    };

    /** Whole days from `From` to `To` (positive when `To` is later); 0 when either is not a day. */
    public static DaysBetween = (From: string, To: string): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            if (AppTaskRules.IsDay(From) === false || AppTaskRules.IsDay(To) === false) { return 0; }
            return Math.round((AppTaskRules.MsOf(To) - AppTaskRules.MsOf(From)) / 86400000);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.DaysBetween");
            return 0;
        }
    };

    /** "Fri 3 Oct" - English words, the same on every machine. */
    public static DayLabel = (Day: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppTaskRules.IsDay(Day) === false) { return ""; }
            const At: Date = new Date(AppTaskRules.MsOf(Day));
            return AppTaskRules.Days[At.getUTCDay()] + " " + String(At.getUTCDate()) + " "
                + AppTaskRules.Months[At.getUTCMonth()];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.DayLabel");
            return "";
        }
    };

    /**
     * The epoch ms of midnight UTC on a day already checked by `IsDay`.
     * DOCUMENTED EXCEPTION: pure arithmetic on a validated string; every caller is guarded.
     * ERROR-SUPPRESSED-JUSTIFIED: pure deterministic helper - guarded at every call site.
     */
    private static MsOf = (Day: string): number => {
        return Date.UTC(Number(Day.substring(0, 4)), Number(Day.substring(5, 7)) - 1, Number(Day.substring(8, 10)));
    };

    // ── State and bucket ─────────────────────────────────────────────────────────────────────

    /**
     * The state as the page treats it: "" (an old row) is open, and a snooze whose date has come
     * is open again - ⚠ the service does not have to rewrite the row for it to come back.
     */
    public static StateOf = (Row: AppRow, Today: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "open"; }
        try {
            const State: string = nz(Row.State);
            if (State === "done" || State === "dismissed") { return State; }
            if (State === "snoozed") {
                if (AppTaskRules.IsDay(Row.Until) === true && Row.Until <= Today) { return "open"; }
                return "snoozed";
            }
            return "open";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.StateOf");
            return "open";
        }
    };

    /** "ai" | "you" | "reply" | "reconnect" - an old row is the harvest's. */
    public static SourceOf = (Row: AppRow): string => {
        if (getCoreUtils().KErrorMode === true) { return "ai"; }
        try {
            if (Row.Source === "you" || Row.Source === "reply" || Row.Source === "reconnect") { return Row.Source; }
            if (Row.Source.length === 0 && Row.Id.indexOf("r-") === 0) { return "reply"; }
            if (Row.Source.length === 0 && Row.Id.indexOf(AppTaskApi.ReconnectPrefix) === 0) { return "reconnect"; }
            return "ai";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.SourceOf");
            return "ai";
        }
    };

    public static IsReply = (Row: AppRow): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return AppTaskRules.SourceOf(Row) === "reply";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.IsReply");
            return false;
        }
    };

    /** A "Reconnect with Anna?" suggestion (`q-`) - gentle: its own group, no Today strip, no tab count. */
    public static IsReconnect = (Row: AppRow): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return AppTaskRules.SourceOf(Row) === "reconnect";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.IsReconnect");
            return false;
        }
    };

    /**
     * The section a task belongs to on `Today` (the browser's day; `OffsetMin` is the browser's UTC
     * offset, +420 for UTC+7 - see `OffsetOf`).
     * ⚠ PRECEDENCE: decided (done/dismissed) -> snoozed -> THEIR move -> a reply owed (Today when
     * recent, else Replies owed) -> the due date (no date: Older when the evidence is old). A task the
     * other person owes stays in "Waiting on them" even when it is late; its chip still says how late.
     * ⛔ A TASK WITH A DUE DATE NEVER GOES TO OLDER, however old its evidence - a date is a promise.
     */
    public static Bucket = (Row: AppRow, Today: string, OffsetMin: number): string => {
        if (getCoreUtils().KErrorMode === true) { return "nodate"; }
        try {
            const State: string = AppTaskRules.StateOf(Row, Today);
            if (State === "done" || State === "dismissed") { return "done"; }
            if (State === "snoozed") { return "snoozed"; }
            if (AppTaskRules.IsReconnect(Row) === true) { return "reconnect"; }
            if (Row.Owner === "them") { return "waiting"; }
            if (AppTaskRules.IsReply(Row) === true) {
                if (AppTaskRules.IsRecentReply(Row, Today, OffsetMin) === true) { return "today"; }
                return "replies";
            }
            if (AppTaskRules.IsDay(Row.Due) === false) {
                if (AppTaskRules.IsOld(Row, Today) === true) { return "older"; }
                return "nodate";
            }
            if (Row.Due < Today) { return "overdue"; }
            if (Row.Due === Today) { return "today"; }
            if (Row.Due <= AppTaskRules.AddDays(Today, 7)) { return "week"; }
            return "later";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.Bucket");
            return "nodate";
        }
    };

    /** This browser's UTC offset in minutes at `Now` (+420 for UTC+7) - the partner of `TodayLocal`. */
    public static OffsetOf = (Now: Date): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            // ⚠ `0 -`, not unary minus: `-0` in UTC is a different value to strict equality in the suite.
            return 0 - Now.getTimezoneOffset();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.OffsetOf");
            return 0;
        }
    };

    /**
     * The BROWSER's day of the evidence message, or "" when the row has no date. ⚠ The service writes
     * `date` + `time` in UTC (`TaskStateOverlay.ReplyRow`: `toISOString()`), so a message at 20:00Z on
     * the 23rd was sent on the 24th for a UTC+7 owner. No time = midnight UTC.
     */
    public static EvidenceDay = (Row: AppRow, OffsetMin: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppTaskRules.IsDay(Row.Date) === false) { return ""; }
            let Ms: number = AppTaskRules.MsOf(Row.Date);
            if (/^\d{2}:\d{2}$/.test(nz(Row.Time)) === true) {
                Ms = Ms + Number(Row.Time.substring(0, 2)) * 3600000 + Number(Row.Time.substring(3, 5)) * 60000;
            }
            return AppTaskRules.LocalDay(Ms, OffsetMin);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.EvidenceDay");
            return "";
        }
    };

    /**
     * A reply owed whose last message from them is at most `ReplyTodayDays` of the browser's days old.
     * ⚠ No date at all = NOT recent: an age nobody knows is not today's work.
     */
    public static IsRecentReply = (Row: AppRow, Today: string, OffsetMin: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Day: string = AppTaskRules.EvidenceDay(Row, OffsetMin);
            if (Day.length === 0) { return false; }
            return AppTaskRules.DaysBetween(Day, Today) <= AppTaskRules.ReplyTodayDays;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.IsRecentReply");
            return false;
        }
    };

    /** Evidence more than `OlderDays` old (by its date). ⚠ No date = not old - it stays visible. */
    public static IsOld = (Row: AppRow, Today: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (AppTaskRules.IsDay(Row.Date) === false) { return false; }
            return AppTaskRules.DaysBetween(Row.Date, Today) > AppTaskRules.OlderDays;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.IsOld");
            return false;
        }
    };

    /** Still something to do (not done, dismissed or snoozed into the future). */
    public static IsOpen = (Row: AppRow, Today: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return AppTaskRules.StateOf(Row, Today) === "open";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.IsOpen");
            return false;
        }
    };

    // ── Order ────────────────────────────────────────────────────────────────────────────────

    /**
     * Due ascending (no date last), then the OLDEST evidence first - the promise that has waited
     * longest is the one most likely forgotten. A copy; the index keeps its own order.
     */
    public static Sort = (Rows: AppRow[]): AppRow[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            return Rows.slice().sort(AppTaskRules.Compare);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.Sort");
            return [];
        }
    };

    /** Done: the newest decision first, so the one just ticked is on top for its Reopen. */
    public static SortDone = (Rows: AppRow[]): AppRow[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            return Rows.slice().sort(AppTaskRules.CompareDone);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.SortDone");
            return [];
        }
    };

    /**
     * DOCUMENTED EXCEPTION: a comparator over values in hand; `Sort` is guarded.
     * ERROR-SUPPRESSED-JUSTIFIED: pure deterministic helper - guarded at every call site.
     */
    public static Compare = (A: AppRow, B: AppRow): number => {
        const Ad: string = AppTaskRules.DueKey(A);
        const Bd: string = AppTaskRules.DueKey(B);
        if (Ad !== Bd) { return AppTaskRules.Cmp(Ad, Bd); }
        const Ae: string = A.Date + " " + A.Time;
        const Be: string = B.Date + " " + B.Time;
        if (Ae !== Be) { return AppTaskRules.Cmp(Ae, Be); }
        return AppTaskRules.Cmp(A.Label, B.Label);
    };

    /** DOCUMENTED EXCEPTION: comparator. ERROR-SUPPRESSED-JUSTIFIED: pure - `SortDone` is guarded. */
    public static CompareDone = (A: AppRow, B: AppRow): number => {
        if (A.DecidedIso !== B.DecidedIso) { return AppTaskRules.Cmp(B.DecidedIso, A.DecidedIso); }
        return AppTaskRules.Compare(A, B);
    };

    /** DOCUMENTED EXCEPTION: pure. ERROR-SUPPRESSED-JUSTIFIED: no-date sorts after every date. */
    private static DueKey = (Row: AppRow): string => {
        if (/^\d{4}-\d{2}-\d{2}$/.test(Row.Due) === true) { return Row.Due; }
        return "9999-99-99";
    };

    /** DOCUMENTED EXCEPTION: pure. ERROR-SUPPRESSED-JUSTIFIED: plain code-unit order, no locale. */
    private static Cmp = (A: string, B: string): number => {
        if (A < B) { return -1; }
        if (A > B) { return 1; }
        return 0;
    };

    /** Replies owed: the NEWEST last message first (owner, 2026-09-26) - a copy. */
    public static SortNewest = (Rows: AppRow[]): AppRow[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            return Rows.slice().sort(AppTaskRules.CompareNewest);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.SortNewest");
            return [];
        }
    };

    /** DOCUMENTED EXCEPTION: comparator. ERROR-SUPPRESSED-JUSTIFIED: pure - `SortNewest` is guarded. */
    public static CompareNewest = (A: AppRow, B: AppRow): number => {
        const Ae: string = A.Date + " " + A.Time;
        const Be: string = B.Date + " " + B.Time;
        if (Ae !== Be) { return AppTaskRules.Cmp(Be, Ae); }
        return AppTaskRules.Cmp(A.Label + A.Contact, B.Label + B.Contact);
    };

    /** The non-empty sections, in `Order`, each sorted. */
    public static Groups = (Rows: AppRow[], Today: string, OffsetMin: number): AppTaskGroup[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const By: Record<string, AppRow[]> = {};
            for (const One of Rows) {
                const Key: string = AppTaskRules.Bucket(One, Today, OffsetMin);
                if (By[Key] === undefined) { By[Key] = []; }
                By[Key].push(One);
            }
            const Out: AppTaskGroup[] = [];
            for (const Key of AppTaskRules.Order) {
                if (By[Key] === undefined) { continue; }
                const Group: AppTaskGroup = new AppTaskGroup();
                Group.Key = Key;
                Group.Title = AppTaskRules.Titles[Key];
                if (Key === "done") { Group.Rows = AppTaskRules.SortDone(By[Key]); }
                else if (Key === "replies") { Group.Rows = AppTaskRules.SortNewest(By[Key]); }
                else { Group.Rows = AppTaskRules.Sort(By[Key]); }
                Out.push(Group);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.Groups");
            return [];
        }
    };

    // ── Filters ──────────────────────────────────────────────────────────────────────────────

    /** Mine = owner me/both/"" (an unknown owner is assumed to be the owner's own). */
    public static IsMine = (Row: AppRow): boolean => {
        if (getCoreUtils().KErrorMode === true) { return true; }
        try {
            return Row.Owner !== "them";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.IsMine");
            return true;
        }
    };

    /**
     * The rows `Who` (mine | theirs | all) keeps whose label, value or contact contain every word of
     * `Search` - folded like the main search (`AppSearch.Fold`), so "bjorn" finds "Björn".
     */
    public static Filter = (Rows: AppRow[], Who: string, Search: string): AppRow[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Words: string[] = AppSearch.Fold(Search).split(/\s+/);
            const Out: AppRow[] = [];
            for (const One of Rows) {
                if (Who === "mine" && AppTaskRules.IsMine(One) === false) { continue; }
                if (Who === "theirs" && AppTaskRules.IsMine(One) === true) { continue; }
                if (AppTaskRules.Matches(One, Words) === false) { continue; }
                Out.push(One);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.Filter");
            return [];
        }
    };

    private static Matches = (Row: AppRow, Words: string[]): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Hay: string = AppSearch.Fold(Row.Label + " " + Row.Value + " " + Row.Contact);
            for (const Word of Words) {
                if (Word.length === 0) { continue; }
                if (Hay.indexOf(Word) < 0) { return false; }
            }
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.Matches");
            return false;
        }
    };

    // ── What a row says ──────────────────────────────────────────────────────────────────────

    /** "Overdue 3 d" / "Today" / "Tomorrow" / "Fri 3 Oct", or no chip ("") without a due date. */
    public static Chip = (Row: AppRow, Today: string): AppTaskChip => {
        if (getCoreUtils().KErrorMode === true) { return new AppTaskChip(); }
        try {
            const Out: AppTaskChip = new AppTaskChip();
            if (AppTaskRules.IsDay(Row.Due) === false) { return Out; }
            const Days: number = AppTaskRules.DaysBetween(Today, Row.Due);
            if (Days < 0) { Out.Text = "Overdue " + String(-Days) + " d"; Out.Cls = "overdue"; return Out; }
            if (Days === 0) { Out.Text = "Today"; Out.Cls = "today"; return Out; }
            if (Days === 1) { Out.Text = "Tomorrow"; Out.Cls = "soon"; return Out; }
            Out.Text = AppTaskRules.DayLabel(Row.Due);
            Out.Cls = "later";
            if (Days <= 7) { Out.Cls = "soon"; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.Chip");
            return new AppTaskChip();
        }
    };

    /**
     * The contact page strip's chip (design F): "3 d late" (red), "Today" (blue), else the date as
     * `Chip` writes it ("Tomorrow", "Fri 3 Oct"); no chip without a due date. `Today` is the BROWSER's
     * day (`TodayLocal`) - the page cannot know it, so the page ships a neutral chip and this recolours it.
     */
    public static StripChip = (Due: string, Today: string): AppTaskChip => {
        if (getCoreUtils().KErrorMode === true) { return new AppTaskChip(); }
        try {
            const Probe: AppRow = new AppRow();
            Probe.Due = nz(Due);
            const Out: AppTaskChip = AppTaskRules.Chip(Probe, Today);
            if (Out.Cls === "overdue") { Out.Text = String(-AppTaskRules.DaysBetween(Today, Due)) + " d late"; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.StripChip");
            return new AppTaskChip();
        }
    };

    /** How old the evidence is: "today", "3 d ago", "5 wk ago", "4 mo ago", or "". */
    public static AgeOf = (Row: AppRow, Today: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppTaskRules.IsDay(Row.Date) === false) { return ""; }
            const Days: number = AppTaskRules.DaysBetween(Row.Date, Today);
            if (Days <= 0) { return "today"; }
            if (Days < 14) { return String(Days) + " d ago"; }
            if (Days < 60) { return String(Math.round(Days / 7)) + " wk ago"; }
            if (Days < 730) { return String(Math.round(Days / 30)) + " mo ago"; }
            return String(Math.round(Days / 365)) + " yr ago";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.AgeOf");
            return "";
        }
    };

    /** The owner hint: "your move", "their move", "both", or "" when nobody said. */
    public static OwnerHint = (Row: AppRow): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Row.Owner === "me") { return "your move"; }
            if (Row.Owner === "them") { return "their move"; }
            if (Row.Owner === "both") { return "both"; }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.OwnerHint");
            return "";
        }
    };

    /** The owner hint's tooltip - the SAME sentence the contact page's strip carries (`AppTaskTips`). */
    public static OwnerTip = (Row: AppRow): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppTaskTips.Explain(Row.Owner, AppTaskTips.FirstName(nz(Row.Contact)));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.OwnerTip");
            return "";
        }
    };

    /** What the row is called: "Reply to <name>" for a reply owed, else its label. */
    public static TitleOf = (Row: AppRow): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppTaskRules.IsReply(Row) === true) { return "Reply to " + AppTaskRules.NameOf(Row); }
            if (Row.Label.length > 0) { return Row.Label; }
            return Row.Value;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.TitleOf");
            return "";
        }
    };

    /** The contact's name, never a blank. */
    public static NameOf = (Row: AppRow): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Row.Contact).trim().length > 0) { return Row.Contact.trim(); }
            return "this contact";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.NameOf");
            return "";
        }
    };

    /** The route to the evidence message: `#c/<href>/<anchor>`, or the chat alone. */
    public static LinkOf = (Row: AppRow): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Row.Href.length === 0) { return ""; }
            let At: string = "#c/" + encodeURIComponent(Row.Href);
            if (Row.Anchor.length > 0) { At = At + "/" + encodeURIComponent(Row.Anchor); }
            return At;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.LinkOf");
            return "";
        }
    };

    // ── Counts and the Today strip ───────────────────────────────────────────────────────────

    /**
     * The number on the Tasks tab. ⚠ OLD INDEX: exactly today's (`Tasks.length`). New index: the
     * OPEN tasks only - the rows now include done and snoozed ones, which are not "to do".
     */
    public static TabCount = (Index: AppIndex, Today: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppTaskRules.Enabled(Index) === false) { return String(Index.Tasks.length); }
            let Open: number = 0;
            for (const One of Index.Tasks) {
                // ⚠ A RECONNECT SUGGESTION IS NOT "TO DO" - it never raises the tab's number.
                if (AppTaskRules.IsOpen(One, Today) === true && AppTaskRules.IsReconnect(One) === false) { Open = Open + 1; }
            }
            return String(Open);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.TabCount");
            return "";
        }
    };

    /**
     * Each contact's "N to do" (`AppContact.OpenTasks`) counted again from the task rows, by the Tasks tab's own rule (open,
     * never a reconnect suggestion). Answers whether any card's number changed. ⚠ OLD INDEX: nothing is touched (its rows
     * carry no state). ⭐ (verifier, 2026-09-30) ON THE WIDE SCREEN THE LIST STAYS BESIDE THE OPEN CHAT, so a tick there left
     * the row saying "3 to do" beside the chat's "2 open" until the next rebuild.
     */
    public static RecountOpen = (Index: AppIndex, Today: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (AppTaskRules.Enabled(Index) === false) { return false; }
            // ⭐ TASKS ARE OBJECTS (2026-11-02): the index's rows become `AppTask`s and the list counts them.
            const List: AppTaskList = new AppTaskList();
            for (const One of Index.Tasks) { List.Add(AppTask.FromIndexRow(One)); }
            const Open: Record<string, number> = List.ToDoByHref(Today);
            let Changed: boolean = false;
            for (const Who of Index.Contacts) {
                if (Who.Href.length === 0 || Who.IsGroup === true) { continue; }
                const Now: number = Open[Who.Href] || 0;
                if (Who.OpenTasks !== Now) { Who.OpenTasks = Now; Changed = true; }
            }
            return Changed;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.RecountOpen");
            return false;
        }
    };

    /**
     * What is due NOW and is the owner's to do: overdue, due today, and the RECENT replies owed (the
     * same rule as the Today section - `Bucket`) - at most `StripMax`, overdue first. ⚠ Nothing at all
     * for an index without the new fields.
     */
    public static Strip = (Index: AppIndex, Today: string, OffsetMin: number): AppTaskStrip => {
        if (getCoreUtils().KErrorMode === true) { return new AppTaskStrip(); }
        try {
            const Out: AppTaskStrip = new AppTaskStrip();
            if (AppTaskRules.Enabled(Index) === false) { return Out; }
            const Over: AppRow[] = [];
            const Now: AppRow[] = [];
            for (const One of Index.Tasks) {
                const Key: string = AppTaskRules.Bucket(One, Today, OffsetMin);
                if (Key === "overdue") { Over.push(One); }
                if (Key === "today") { Now.push(One); }
            }
            const All: AppRow[] = AppTaskRules.Sort(Over).concat(AppTaskRules.Sort(Now));
            Out.Count = All.length;
            Out.Rows = All.slice(0, AppTaskRules.StripMax);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.Strip");
            return new AppTaskStrip();
        }
    };

    // ── Changing a row ───────────────────────────────────────────────────────────────────────

    /** The snooze day for a fixed choice: "tomorrow", or "nextweek" (the coming Monday). */
    public static SnoozeDay = (Choice: string, Today: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppTaskRules.IsDay(Today) === false) { return ""; }
            if (Choice === "tomorrow") { return AppTaskRules.AddDays(Today, 1); }
            if (Choice !== "nextweek") { return ""; }
            const Weekday: number = new Date(AppTaskRules.MsOf(Today)).getUTCDay();
            let Ahead: number = (8 - Weekday) % 7;
            if (Ahead === 0) { Ahead = 7; }
            return AppTaskRules.AddDays(Today, Ahead);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.SnoozeDay");
            return "";
        }
    };

    /** A snooze must be to a real day AFTER today, or it would not hide anything. "" = fine. */
    public static SnoozeRefusal = (Until: string, Today: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppTaskRules.IsDay(Until) === false) { return "Pick a day to snooze it until."; }
            if (Until <= Today) { return "Pick a day after today."; }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.SnoozeRefusal");
            return "";
        }
    };

    /** A copy of `Row`, so an optimistic change can be taken back exactly. */
    public static Copy = (Row: AppRow): AppRow => {
        if (getCoreUtils().KErrorMode === true) { return new AppRow(); }
        try {
            const Out: AppRow = new AppRow();
            AppTaskRules.Merge(Out, Row);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.Copy");
            return new AppRow();
        }
    };

    /** Every field of `From` onto `Into` - the server's answer over the optimistic row, or a revert. */
    public static Merge = (Into: AppRow, From: AppRow): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Into.Label = From.Label; Into.Value = From.Value; Into.Group = From.Group;
            Into.Kind = From.Kind; Into.Type = From.Type; Into.Status = From.Status;
            Into.Date = From.Date; Into.Time = From.Time; Into.Anchor = From.Anchor;
            Into.Link = From.Link; Into.Contact = From.Contact; Into.Href = From.Href;
            Into.PersonKey = From.PersonKey; Into.Money = From.Money; Into.Id = From.Id;
            Into.Owner = From.Owner; Into.Due = From.Due; Into.State = From.State;
            Into.Until = From.Until; Into.Source = From.Source; Into.DecidedIso = From.DecidedIso;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.Merge");
        }
    };

    /**
     * ⚠ A SERVER ANSWER FILLS IN, IT NEVER BLANKS: the answer's `task` may carry fewer fields than
     * the index row (no contact, no href), so only what it states replaces what the row had.
     */
    public static Adopt = (Into: AppRow, From: AppRow): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Keep: AppRow = AppTaskRules.Copy(Into);
            AppTaskRules.Merge(Into, From);
            if (From.Contact.length === 0) { Into.Contact = Keep.Contact; }
            if (From.Href.length === 0) { Into.Href = Keep.Href; }
            if (From.PersonKey.length === 0) { Into.PersonKey = Keep.PersonKey; }
            if (From.Anchor.length === 0) { Into.Anchor = Keep.Anchor; }
            if (From.Date.length === 0) { Into.Date = Keep.Date; Into.Time = Keep.Time; }
            if (From.Id.length === 0) { Into.Id = Keep.Id; }
            if (From.Label.length === 0 && From.Value.length === 0) { Into.Label = Keep.Label; Into.Value = Keep.Value; }
            // ⚠ NO ACTION IN THE CONTRACT CLEARS THESE, so an empty one in the answer is an omission.
            if (From.Owner.length === 0) { Into.Owner = Keep.Owner; }
            if (From.Due.length === 0) { Into.Due = Keep.Due; }
            if (From.Source.length === 0) { Into.Source = Keep.Source; }
            if (From.Group.length === 0) { Into.Group = Keep.Group; Into.Kind = Keep.Kind; Into.Type = Keep.Type; }
            if (From.Money === null) { Into.Money = Keep.Money; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.Adopt");
        }
    };

    /** The optimistic change `Action` makes to `Row` before the service has answered. */
    public static Apply = (Row: AppRow, Action: string, Until: string, NowIso: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Action === "done") { Row.State = "done"; Row.Until = ""; }
            if (Action === "dismiss") { Row.State = "dismissed"; Row.Until = ""; }
            if (Action === "reopen") { Row.State = "open"; Row.Until = ""; }
            if (Action === "snooze") { Row.State = "snoozed"; Row.Until = Until; }
            Row.DecidedIso = NowIso;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.Apply");
        }
    };

    /** The row with this server id, or null. ⛔ Compared in a loop - an id never enters a selector. */
    public static FindById = (Index: AppIndex, Id: string): AppRow | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (nz(Id).length === 0) { return null; }
            for (const One of Index.Tasks) {
                if (One.Id === Id) { return One; }
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.FindById");
            return null;
        }
    };

    /** What the owner said when a change landed: "Marked done." etc. */
    public static DoneWord = (Action: string, Until: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Action === "done") { return "Marked done."; }
            if (Action === "reopen") { return "Back on the list."; }
            if (Action === "dismiss") { return "Dismissed."; }
            if (Action === "snooze") { return "Snoozed until " + AppTaskRules.DayLabel(Until) + "."; }
            if (Action === "add") { return "Task added."; }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.DoneWord");
            return "";
        }
    };

    // ── The AI panel ─────────────────────────────────────────────────────────────────────────

    /** The first word of a name - "Anna" of "Anna Karlsson"; "" for a number or a blank. */
    public static FirstName = (Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppTaskTips.FirstName(Title);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.FirstName");
            return "";
        }
    };

    /**
     * The chips above the composer. ⚠ OLD INDEX: exactly `AppAssistApi.Presets`. With tasks on, the
     * contract's task questions too (`AppTaskApi.AssistPresets`), "them" made the contact's first
     * name when there is one: "What do I owe Anna?".
     */
    public static Presets = (Title: string, On: boolean): string[] => {
        if (getCoreUtils().KErrorMode === true) { return AppAssistApi.Presets.slice(); }
        try {
            const Out: string[] = AppAssistApi.Presets.slice();
            if (On === false) { return Out; }
            const Who: string = AppTaskRules.FirstName(Title);
            for (const One of AppTaskApi.AssistPresets) {
                if (Who.length === 0) { Out.push(One); continue; }
                Out.push(One.replace(/\bthem\b/, Who));
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.Presets");
            return AppAssistApi.Presets.slice();
        }
    };

    /** The `/tasks` answer's row as an `AppRow` - `AppTaskRules.Adopt` lays it over the index row. */
    public static RowFrom = (Task: AppTaskRow): AppRow => {
        if (getCoreUtils().KErrorMode === true) { return new AppRow(); }
        try {
            const Out: AppRow = new AppRow();
            Out.Id = Task.Id; Out.Source = Task.Source; Out.Label = Task.Label; Out.Value = Task.Value;
            Out.Owner = Task.Owner; Out.Due = Task.Due; Out.State = Task.State; Out.Until = Task.Until;
            Out.DecidedIso = Task.DecidedIso; Out.Date = Task.Date; Out.Time = Task.Time; Out.Anchor = Task.Anchor;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.RowFrom");
            return new AppRow();
        }
    };

    // ── Sentences (ink, never the error handler) ─────────────────────────────────────────────

    /**
     * A lost connection or an unreadable answer - `AppTaskApi.Parse`'s `OursWhy`. ⚠ CALM ON PURPOSE,
     * and it says nothing was changed, because the row has just been put back.
     * DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw.
     */
    public static LostWhy = (): string => {
        return "Couldn't reach WACommander just now, so nothing was changed. Try again in a moment.";
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static SignInWhy = (): string => {
        return "Sign in on this browser to change tasks.";
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static BusyWhy = (): string => {
        return "Still saving the last change to this task - one moment.";
    };

    /** What "Reply about this task" asks the AI panel. ⛔ Asking only - the Send buttons send. */
    public static ReplyAsk = (Row: AppRow): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppTaskRules.IsReconnect(Row) === true) {
                return ("Help me write a short, warm message to get back in touch with " + AppTaskRules.NameOf(Row)
                    + ".").substring(0, AppAssistApi.MaxAsk);
            }
            let About: string = AppTaskRules.TitleOf(Row);
            if (Row.Value.length > 0 && AppTaskRules.IsReply(Row) === false) { About = About + " - " + Row.Value; }
            return ("Help me reply about: " + About).substring(0, AppAssistApi.MaxAsk);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskRules.ReplyAsk");
            return "";
        }
    };
}
